/**
 * Community Post Create Page
 * /community/groups/:groupId/posts/new - 후기 작성 페이지
 */

import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Star, Image as ImageIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { buildLoginPath } from "@/lib/auth-navigation";
import { MarketImageUploader } from "@/components/MarketImageUploader";

const formSchema = z.object({
  title: z.string().min(1, "제목을 입력해주세요").max(200).optional(),
  body: z.string().min(10, "내용은 최소 10자 이상이어야 합니다").max(5000),
  type: z.enum(["review", "notice", "general"]).default("general"),
  rating: z.number().int().min(1).max(5).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface Group {
  id: number;
  title: string;
  slug: string;
}

export default function PostCreatePage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ groupId: string }>("/community/groups/:groupId/posts/new");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [images, setImages] = useState<string[]>([]);

  const groupId = params?.groupId ? parseInt(params.groupId) : null;

  // 그룹 상세 조회
  const { data: group, isLoading: groupLoading } = useQuery<Group>({
    queryKey: ["/api/community/groups", groupId],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}`);
      if (!response.ok) throw new Error("Failed to fetch group");
      return response.json();
    },
    enabled: !!groupId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: "general",
    },
  });

  const postType = watch("type");
  const isReview = postType === "review";

  // 후기 작성
  const createPostMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");

      const token = await user.getIdToken();
      const requestBody = {
        title: data.title || null,
        body: data.body,
        type: data.type,
        rating: isReview ? data.rating || null : null,
      };

      const response = await fetch(`/api/community/groups/${groupId}/posts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "후기 작성에 실패했습니다");
      }

      const post = await response.json();
      return post;
    },
    onSuccess: async (post) => {
      // 이미지가 있으면 업로드
      if (images.length > 0 && post.id) {
        try {
          const token = await user?.getIdToken();
          for (let i = 0; i < images.length; i++) {
            const imageUrl = images[i];
            await fetch(`/api/community/posts/${post.id}/images`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                imageUrl: imageUrl,
                sortOrder: i,
              }),
            });
          }
        } catch (error) {
          console.error("Error uploading images:", error);
          toast({
            title: "이미지 업로드 실패",
            description: "후기는 작성되었지만 일부 이미지 업로드에 실패했습니다.",
            variant: "destructive",
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId, "posts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId] });
      toast({
        title: "후기가 작성되었습니다",
        description: "후기 상세 페이지로 이동합니다.",
      });
      navigate(`/community/posts/${post.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    createPostMutation.mutate(data);
  };

  if (!match || !params || !groupId) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">유효하지 않은 모임 ID입니다</p>
            <Button className="mt-4" onClick={() => navigate("/community/groups")}>
              목록으로
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (groupLoading) {
    return (
      <PageContainer size="read">
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </PageContainer>
    );
  }

  if (!group) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">모임을 찾을 수 없습니다</p>
            <Button className="mt-4" onClick={() => navigate("/community/groups")}>
              목록으로
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">로그인이 필요합니다</p>
            <Button className="mt-4" onClick={() => navigate(buildLoginPath())}>
              로그인하기
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <>
      <Seo
        title={`${group.title} - 후기 작성`}
        description="모임 후기를 작성해주세요"
        path={`/community/groups/${groupId}/posts/new`}
      />
      <PageContainer size="read">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/community/groups/${groupId}`)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">후기 작성</h1>
              <p className="text-muted-foreground">{group.title}</p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>후기 작성</CardTitle>
              <CardDescription>모임에 대한 후기를 작성해주세요</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* 타입 선택 */}
                <div className="space-y-2">
                  <Label htmlFor="type">타입 *</Label>
                  <Select
                    value={watch("type") || "general"}
                    onValueChange={(value) => setValue("type", value as "review" | "notice" | "general")}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">일반 게시글</SelectItem>
                      <SelectItem value="review">후기</SelectItem>
                      <SelectItem value="notice">공지</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* 제목 (선택) */}
                <div className="space-y-2">
                  <Label htmlFor="title">제목 (선택)</Label>
                  <Input
                    id="title"
                    {...register("title")}
                    placeholder="제목을 입력해주세요"
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive">{errors.title.message}</p>
                  )}
                </div>

                {/* 평점 (후기 타입일 때만) */}
                {isReview && (
                  <div className="space-y-2">
                    <Label htmlFor="rating">평점 *</Label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => setValue("rating", rating)}
                          className={`p-2 rounded ${
                            watch("rating") === rating
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted hover:bg-muted/80"
                          }`}
                        >
                          <Star
                            className={`w-5 h-5 ${
                              watch("rating") && watch("rating")! >= rating
                                ? "fill-yellow-400 text-yellow-400"
                                : ""
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    {errors.rating && (
                      <p className="text-sm text-destructive">{errors.rating.message}</p>
                    )}
                  </div>
                )}

                {/* 내용 */}
                <div className="space-y-2">
                  <Label htmlFor="body">내용 *</Label>
                  <Textarea
                    id="body"
                    {...register("body")}
                    placeholder="후기 내용을 입력해주세요"
                    rows={10}
                  />
                  {errors.body && (
                    <p className="text-sm text-destructive">{errors.body.message}</p>
                  )}
                </div>

                {/* 이미지 업로드 */}
                <div className="space-y-2">
                  <Label>사진 (선택)</Label>
                  <MarketImageUploader
                    initialImages={images}
                    onImagesChange={setImages}
                    maxImages={10}
                  />
                  <p className="text-sm text-muted-foreground">
                    최대 10장까지 업로드할 수 있습니다
                  </p>
                </div>

                {/* 저장 버튼 */}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/community/groups/${groupId}`)}
                  >
                    취소
                  </Button>
                  <Button type="submit" disabled={createPostMutation.isPending}>
                    <Save className="w-4 h-4 mr-2" />
                    {createPostMutation.isPending ? "작성 중..." : "작성하기"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </>
  );
}
